import {registerActorTarget} from '../game/actorTargetInput';
import Phaser from 'phaser';
import {actorRoleCue,type ActorRoleCue} from '../game/actorRoleCue';
import {drawActorRoleCue} from '../game/actorRoleGraphics';

import { HunterRig } from '../game/hunterRig';
import { Locomotion, surfaceHeight } from '../game/locomotion';
import { GAME_MAPS } from '../game/worldMaps';
import { smooth } from '../game/motionPose';
import { combatAudio } from '../game/combatAudio';
import { GEAR } from '../game/character';
import {classSkillFx} from '../game/classSkillFx';
import type { OnlinePlayer } from './online';

export class RemoteActor {
  readonly container: Phaser.GameObjects.Container;
  readonly rig: HunterRig;
  private readonly name: Phaser.GameObjects.Text;
  private readonly body: Locomotion;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly roleMark:Phaser.GameObjects.Graphics;
  private roleScale=1;
  private snapshot: OnlinePlayer;
  private lastAction = 0;
  private heightVelocity = 0;
  private receivedAt = 0;
  get nameplate():Phaser.GameObjects.Text {return this.name;}
  constructor(private scene: Phaser.Scene, player: OnlinePlayer,select?:(player:OnlinePlayer)=>void) {
    this.snapshot = player;
    this.lastAction = player.actionAt ?? 0; // joining a room must not replay its historical action
    this.body = new Locomotion(player.x, GAME_MAPS[(player.mapId ?? 'village') as keyof typeof GAME_MAPS]);
    this.body.depth = player.depth ?? 0; this.body.height = player.height ?? 0;
    this.rig = new HunterRig(scene);
    this.shadow = scene.add.ellipse(0, 0, 42, 9, 0x020407, .4);
    this.name = scene.add.text(0, -106, '', { fontFamily: 'Noto Sans Thai', fontSize: '12px', color: '#bdebee', stroke: '#060a0b', strokeThickness: 5, shadow:{offsetX:0,offsetY:2,color:'#000000',blur:4,fill:true,stroke:true} }).setOrigin(.5);
    this.name.setDepth(9999);
    this.container = scene.add.container(0, 0, [this.shadow,this.roleMark=scene.add.graphics(),this.rig.container]);
    if(select){this.container.setInteractive(new Phaser.Geom.Rectangle(-40,-170,80,180),Phaser.Geom.Rectangle.Contains);registerActorTarget(scene,this.container,'player',player.id,()=>select(this.snapshot),()=>(this.snapshot.hp??1)>0,()=>({x:this.container.x-40,y:this.container.y-170,width:80,height:180}));}
    this.receive(player);
  }
  showRole(cue:ActorRoleCue):void {drawActorRoleCue(this.roleMark,cue,this.roleScale);this.roleMark.setPosition(0,this.shadow.y);}
  receive(player: OnlinePlayer): void {
    if(Math.abs(player.x-this.body.x)>300){this.body.x=player.x;this.body.depth=player.depth??0;this.body.height=player.height??0;}
    const elapsed = Math.max(.05, (this.scene.time.now - this.receivedAt) / 1000);
    this.heightVelocity = ((player.height ?? 0) - (this.snapshot.height ?? 0)) / elapsed;
    this.receivedAt = this.scene.time.now; this.snapshot = player;
    this.name.setText(`${player.name} • Lv.${player.progression.level}`);
    const tint = player.profile?.equipment.outfit === 'forest' ? 0x95b79b : player.profile?.equipment.outfit === 'crimson' ? 0xd1a0a0 : 0xc6d2cf;
    const outfit=GEAR.find(g=>g.id===player.profile?.equipment.outfit),weapon=GEAR.find(g=>g.id===player.profile?.equipment.weapon),appearance=player.profile?.appearance;
    this.rig.dress(outfit?.tint??tint,weapon?.tint??0xffffff,weapon?.kind??'knife',outfit?.id??'indigo',appearance?.preset??'phu-bao-thai-ban',appearance,player.profile?.profession??'villager',player.profile?.equipment.charm??'');
    this.rig.pet(player.pet,player.spiritSpecimen);this.rig.keepsake(player.journey?.keepsake);
    if ((player.actionAt ?? 0) > this.lastAction) {
      this.lastAction = player.actionAt!;
      if (player.actionKind === 'dodge') this.body.dodge(); else this.rig.attack();
      if(player.actionKind==='cast'&&player.actionSkill)classSkillFx(this.scene,player.actionSkill,this.container.x,this.container.y,player.facing??1);
      else if(player.actionKind==='slash'&&(weapon?.kind==='bow'||weapon?.kind==='crossbow'||weapon?.kind==='spear'||weapon?.kind==='talisman'))classSkillFx(this.scene,weapon.kind==='spear'?'spear-piercing-thrust':weapon.kind==='talisman'?'shaman-exorcism-talisman':'hunter-arrow',this.container.x,this.container.y,player.facing??1);
      combatAudio.play(player.actionKind === 'dodge' ? 'dodge' : 'slash', .5);
    }
  }
  update(seconds: number, time: number, worldScale: number, ground: number, scale: number): void {
    this.roleScale=scale;
    const previousX = this.body.x, previousDepth = this.body.depth;
    this.body.step(seconds, { axis: 0, depth: 0 }); // maintains dodge timers, never sends predicted position
    this.body.x = smooth(previousX, this.snapshot.x, 16, seconds);
    this.body.depth = smooth(previousDepth, this.snapshot.depth ?? 0, 16, seconds);
    this.body.height = smooth(this.body.height, this.snapshot.height ?? 0, 22, seconds);
    this.body.vx = seconds > 0 ? (this.body.x - previousX) / seconds : 0;
    this.body.vz = seconds > 0 ? (this.body.depth - previousDepth) / seconds : 0;
    this.body.vy = time - this.receivedAt < 180 ? this.heightVelocity : 0;
    this.body.gait += Math.hypot(this.body.vx, this.body.vz) * seconds / 19;
    this.body.facing = this.snapshot.facing ?? (this.body.vx < -3 ? -1 : this.body.vx > 3 ? 1 : this.body.facing);
    const support = this.body.world.surfaces.find(s => this.body.x >= s.left && this.body.x <= s.right && Math.abs(this.body.depth - s.depth) <= s.halfDepth && Math.abs(surfaceHeight(s, this.body.x) - this.body.height) < 6);
    this.body.grounded = this.body.height < 2 || !!support;
    this.rig.setDead((this.snapshot.hp??120)<=0);
    this.rig.setPose(this.snapshot.pose??'stand');
    const airGap=Math.max(0,this.body.height-(support?surfaceHeight(support,this.body.x):0));
    this.rig.update(this.body, seconds, time, scale, this.body.x * worldScale,airGap*this.scene.scale.height/720);
    this.container.setPosition(this.body.x * worldScale, ground + (this.body.depth - this.body.height) * this.scene.scale.height / 720).setDepth(10 + this.body.depth / 100);
    this.name.setPosition(this.container.x,this.container.y-132*scale);
    this.shadow.setY(airGap*this.scene.scale.height/720).setScale(Math.max(.4,1-airGap/250),1);
    this.container.setData('pose', this.body.mode).setData('armAngle', this.rig.armAngle).setData('kneeAngle', this.rig.kneeAngle);
    this.container.setData('attacking', this.rig.attacking);
  }
  destroy(): void { this.name.destroy();this.container.destroy(true); }
}
